import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Spin, Tooltip, Empty, Col, Row, Modal, Input, Button, Card, Tag, Statistic, Collapse, Select, Checkbox, Space, Form, notification, DatePicker } from 'antd';
import type { TabsProps } from 'antd';
import {
    InfoCircleOutlined,
    CalculatorOutlined,
    HistoryOutlined,
    SearchOutlined,
    EditOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FilterOutlined,
    CloseOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    SaveOutlined,
    UndoOutlined,
    ClockCircleOutlined,
    UserOutlined,
    BankOutlined,
    MinusOutlined,
    PlusOutlined,
    ReloadOutlined,
    SyncOutlined,
    CalendarOutlined,
    RightOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import { isEmpty } from 'underscore';
import dayjs from 'dayjs';

import { AppDispatch, CombinedStates } from '../../core/types';
import { IndicatorTargetValue, RecalculateComputedIndicatorPayload, SchoolIndicator, SchoolIndicatorValue } from '../../core/indicator/types';
import { getSchoolIndicatorLogs, getSchoolIndicators, recalculateComputedIndicator, unconfirmIndicatorValue, updateSchoolIndicators } from '../../core/indicator/indicatorApi';
import { frequencyOptions as baseFrequencyOptions, isCollectionPeriodMissed } from '../../core/utils/helpers';

import './schoolIndicatorsData.scss';
import MissingIndicatorsButton from './MissingIndicatorsButton';

const { Panel } = Collapse;
const { Option } = Select;

const actionLabels: Record<string, string> = {
    created: 'Sukurta',
    updated: 'Atnaujinta',
    deleted: 'Ištrinta',
    computed: 'Apskaičiuota',
    compute_error: 'Skaičiavimo klaida',
};

const frequencyOptions = Object.fromEntries(
    Object.entries(baseFrequencyOptions).map(([key, value]) => [key, { ...value, icon: <CalendarOutlined /> }]),
);

const allowedRolesForCritical = [1, 2, 3, 4];
const allowedRolesForUnconfirm = [1, 2, 3, 4];

interface FilterOptions {
    searchTerm: string;
    valueStatus: 'all' | 'filled' | 'empty';
    indicatorType: 'all' | 'computed' | 'manual';
    criticalOnly: boolean;
    frequency: 'all' | 'school_year' | 'yearly' | 'quarterly' | 'monthly' | 'once' | 'twice_a_year_july_january' | 'twice_a_year_may_october' | 'semester';
}

interface EditableIndicator {
    id: number;
    value: string | null;
    unit: string | null;
    value_id: number | null;
    isEditing: boolean;
    originalValue: string | null;
}

interface SchoolIndicatorsDataProps {
    scope?: 'school' | 'education_center' | 'education_department';
}

const SchoolIndicatorsData = ({ scope }: SchoolIndicatorsDataProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<string>('');
    const [logModalVisible, setLogModalVisible] = useState(false);
    const [filterPanelVisible, setFilterPanelVisible] = useState(false);
    const [tabsCollapsed, setTabsCollapsed] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingIndicators, setEditingIndicators] = useState<Record<number, EditableIndicator>>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [recalculatingIndicatorId, setRecalculatingIndicatorId] = useState<number | null>(null);
    const [unconfirmingIndicatorId, setUnconfirmingIndicatorId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [indicatorsToConfirm, setIndicatorsToConfirm] = useState<Set<number>>(new Set());
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        searchTerm: '',
        valueStatus: 'all',
        indicatorType: 'all',
        criticalOnly: false,
        frequency: 'all',
    });

    const { singleSchool } = useSelector((state: CombinedStates) => state.school);
    const {
        schoolIndicators,
        schoolIndicatorLogs,
        isFetching,
    } = useSelector((state: CombinedStates) => state.indicator);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    // Check if user has roles 1, 2, 3, or 4
    // const canSelectDate = userData?.role_id && [1, 2, 3, 4].includes(userData.role_id);
    const canSelectDate = true;
    const canUnconfirmConfirmation = allowedRolesForUnconfirm.includes(userData?.role_id || 0);

    // Debounce search term with 0.5 second delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const payload: any = { scope };

        if (scope === 'school' && singleSchool?.id) {
            payload.school_id = singleSchool.id;
        }

        // Add category_id if activeTab is set
        if (activeTab) {
            payload.category_id = parseInt(activeTab, 10);
        }

        // Add date parameter if user can select date
        if (canSelectDate) {
            payload.date = selectedDate.format('YYYY-MM-DD');
        }

        dispatch(getSchoolIndicators(payload));
    }, [dispatch, singleSchool, scope, activeTab, canSelectDate, selectedDate]);

    // Recursively walk all categories (and their nested children) calling fn on each
    const forEachCategory = (categories: SchoolIndicator[], fn: (cat: SchoolIndicator) => void) => {
        categories.forEach(cat => {
            fn(cat);
            if (cat.children) {
                forEachCategory(cat.children, fn);
            }
        });
    };

    // Set active tab when data is loaded or when current active tab is not in available categories
    useEffect(() => {
        if (schoolIndicators.length > 0) {
            const availableTabIds: string[] = [];
            forEachCategory(schoolIndicators, cat => {
                availableTabIds.push(cat.id.toString());
            });

            // If no active tab is set, or current active tab is not in available categories, set to first available
            if (!activeTab || !availableTabIds.includes(activeTab)) {
                setActiveTab(schoolIndicators[0]?.id?.toString() || '');
            }
        }
    }, [schoolIndicators, activeTab]);

    // Initialize form values when data changes
    useEffect(() => {
        if (!schoolIndicators || schoolIndicators.length === 0) return;

        const updatedValues: Record<string, string | undefined> = {};

        forEachCategory(schoolIndicators, (category) => {
            category.indicators?.forEach((indicator) => {
                updatedValues[`indicator_${indicator.id}`] = indicator.value ?? undefined;
            });
        });

        form.setFieldsValue(updatedValues);
    }, [schoolIndicators, form]);

    const getUnitSymbol = (unit?: string | null) => {
        if (!unit) return '';
        if (unit.toLowerCase().includes('eur')) return '€';
        if (unit.toLowerCase().includes('proc') || unit === '%') return '%';
        if (unit.toLowerCase().includes('vnt')) return 'vnt.';
        if (unit.toLowerCase().includes('taip_ne')) return 'Taip / ne';
        if (unit.toLowerCase() === 'text') return 'Tekstas';
        return unit;
    };

    // Recursively apply a filter function to a category tree and its children
    const filterCategoryIndicators = (
        categories: SchoolIndicator[],
        filterFn: (indicator: any) => boolean,
    ): SchoolIndicator[] => {
        const hasIndicators = (cat: SchoolIndicator): boolean => cat.indicators.length > 0
            || (cat.children != null && cat.children.some(hasIndicators));

        const mapCategory = (category: SchoolIndicator): SchoolIndicator => {
            const result: SchoolIndicator = {
                ...category,
                indicators: (category.indicators || []).filter(filterFn),
            };
            if (category.children) {
                result.children = category.children.map(mapCategory);
            }
            return result;
        };

        return categories.map(mapCategory).filter(hasIndicators);
    };

    // Filter indicators based on search term and filter options
    const filteredSchoolIndicators = useMemo(() => {
        let filtered = schoolIndicators;

        // Apply search filter
        if (debouncedSearchTerm.trim()) {
            const searchLower = debouncedSearchTerm.toLowerCase();
            filtered = filterCategoryIndicators(filtered, (indicator) => indicator.title.toLowerCase().includes(searchLower)
                    || indicator.description?.toLowerCase().includes(searchLower)
                    || indicator.id.toString().includes(debouncedSearchTerm));
        }

        // Apply value status filter
        if (filterOptions.valueStatus !== 'all') {
            filtered = filterCategoryIndicators(filtered, indicator => {
                const hasValue = indicator.value !== null && indicator.value !== undefined && indicator.value !== '';
                return filterOptions.valueStatus === 'filled' ? hasValue : !hasValue;
            });
        }

        // Apply indicator type filter
        if (filterOptions.indicatorType !== 'all') {
            filtered = filterCategoryIndicators(filtered, indicator => {
                const isComputed = indicator.is_computed;
                return filterOptions.indicatorType === 'computed' ? isComputed : !isComputed;
            });
        }

        // Apply critical filter
        if (filterOptions.criticalOnly) {
            filtered = filterCategoryIndicators(filtered, indicator => indicator.is_critical);
        }

        // Apply frequency filter
        if (filterOptions.frequency !== 'all') {
            filtered = filterCategoryIndicators(filtered, indicator => indicator.input_frequency === filterOptions.frequency);
        }

        return filtered;
    }, [schoolIndicators, debouncedSearchTerm, filterOptions]);

    // Calculate statistics for the current filtered data
    const statistics = useMemo(() => {
        const collectIndicators = (categories: SchoolIndicator[]): SchoolIndicatorValue[] => {
            const result: SchoolIndicatorValue[] = [];
            forEachCategory(categories, cat => {
                result.push(...(cat.indicators || []));
            });
            return result;
        };
        const allIndicators = collectIndicators(filteredSchoolIndicators);
        const total = allIndicators.length;
        const filled = allIndicators.filter(ind => ind.value !== null && ind.value !== undefined && ind.value !== '').length;
        const computed = allIndicators.filter(ind => ind.is_computed).length;
        const critical = allIndicators.filter(ind => ind.is_critical).length;

        return { total, filled, computed, critical, empty: total - filled };
    }, [filteredSchoolIndicators]);

    const formatValue = (value: string | null | undefined, unit?: string | null) => {
        if (value === null || value === undefined || value === '') {
            return <span className="no-value">Nenurodyta</span>;
        }

        const unitSymbol = getUnitSymbol(unit);

        if (unit === 'taip_ne') {
            return value === '1.000' ? 'Taip' : 'Ne';
        }

        if (unit === 'text') {
            return (
                <span className="indicator-value text-value">
                    <div className="text-content">{value}</div>
                </span>
            );
        }

        return (
            <span className="indicator-value">
                {value}{unitSymbol && <span className="unit-symbol">{unitSymbol}</span>}
            </span>
        );
    };

    const getIndicatorStatus = (indicator: any) => {
        if (indicator.is_computed) {
            return { type: 'processing', icon: <CalculatorOutlined />, text: 'Skaičiuojamas' };
        }
        if (indicator.value !== null && indicator.value !== undefined && indicator.value !== '') {
            return { type: 'success', icon: <CheckCircleOutlined />, text: 'Užpildyta' };
        }
        return { type: 'warning', icon: <ExclamationCircleOutlined />, text: 'Neužpildyta' };
    };

    const getStatusColor = (statusType: string) => {
        switch (statusType) {
        case 'success':
            return 'green';
        case 'warning':
            return 'orange';
        case 'processing':
            return 'purple';
        default:
            return 'default';
        }
    };

    const renderFrequencyDisplay = (frequency: string) => {
        const option = frequencyOptions[frequency as keyof typeof frequencyOptions];
        if (!option) return null;

        // Create shorter display text for the tag
        const getShortLabel = (freq: string) => {
            switch (freq) {
            case 'school_year': return 'Mokslo metai';
            case 'yearly': return 'Metinis';
            case 'quarterly': return 'Ketvirtis';
            case 'monthly': return 'Mėnesinis';
            case 'once': return 'Vienkartinis';
            case 'twice_a_year_july_january': return '2x per metus';
            case 'twice_a_year_may_october': return '2x per metus';
            case 'semester': return 'Pusmetis';
            default: return option.label;
            }
        };

        return (
            <Tooltip title={`${option.description}`}>
                <Tag
                    color={option.color}
                    icon={option.icon}
                    className="frequency-tag"
                    style={{ fontSize: '11px', marginTop: 4 }}>
                    {getShortLabel(frequency)}
                </Tag>
            </Tooltip>
        );
    };

    const renderFrequencyDescription = (frequency: string, description: string) => {
        const option = frequencyOptions[frequency as keyof typeof frequencyOptions];
        if (!option) return null;

        return (
            <Tooltip title={`Duomenys renkami už laikotarpį: ${description}`}>
                <Tag
                    color={option.color}
                    className="frequency-tag"
                    style={{ fontSize: '11px', marginTop: 4 }}>
                    {description}
                </Tag>
            </Tooltip>
        );
    };

    const renderConfirmationTag = (indicator: any) => {
        if (!indicator.confirmed_at) return null;

        const formattedDate = dayjs(indicator.confirmed_at).format('YYYY-MM-DD HH:mm:ss');
        const confirmedBy = indicator.confirmed_by_user_name || 'Nežinomas vartotojas';
        const confirmedByEmail = indicator.confirmed_by_user_email ? ` (${indicator.confirmed_by_user_email})` : '';

        return (
            <Tooltip title={`Patvirtinta: ${formattedDate}\nPatvirtino: ${confirmedBy}${confirmedByEmail}`}>
                <Tag
                    className="confirmation-tag"
                    style={{ fontSize: '11px', marginTop: 0 }}>
                    <CheckCircleOutlined />
                    Patvirtinta {dayjs(indicator.confirmed_at).format('YYYY-MM-DD')}
                </Tag>
            </Tooltip>
        );
    };

    const handleFilterReset = () => {
        setSearchTerm('');
        setFilterOptions({
            searchTerm: '',
            valueStatus: 'all',
            indicatorType: 'all',
            criticalOnly: false,
            frequency: 'all',
        });
    };

    const handleRefreshData = () => {
        const payload: any = { scope };

        if (scope === 'school' && singleSchool?.id) {
            payload.school_id = singleSchool.id;
        }

        // Add category_id if activeTab is set
        if (activeTab) {
            payload.category_id = parseInt(activeTab, 10);
        }

        // Add date parameter if user can select date
        if (canSelectDate) {
            payload.date = selectedDate.format('YYYY-MM-DD');
        }

        dispatch(getSchoolIndicators(payload));
    };

    const formatRecalculatedValue = (value: number | string | null | undefined) => {
        if (value === null || value === undefined || value === '') {
            return 'nėra';
        }
        return String(value);
    };

    const handleRecalculateIndicator = async (indicator: SchoolIndicatorValue) => {
        if (recalculatingIndicatorId !== null) return;

        const effectiveScope = scope || 'school';
        const payload: RecalculateComputedIndicatorPayload = {
            indicator_id: indicator.id,
            scope: effectiveScope,
        };

        if (canSelectDate) {
            payload.date = selectedDate.format('YYYY-MM-DD');
        }

        if (effectiveScope === 'school') {
            if (!singleSchool?.id) {
                notification.error({
                    message: 'Klaida',
                    description: 'Nepavyko nustatyti mokyklos, todėl rodiklio perskaičiuoti negalima',
                });
                return;
            }
            payload.school_id = singleSchool.id;
        }

        setRecalculatingIndicatorId(indicator.id);
        try {
            const result = await dispatch(recalculateComputedIndicator(payload)).unwrap();
            const valueChanged = result.updated || result.created;
            const previous = formatRecalculatedValue(result.previous_value);
            const calculated = formatRecalculatedValue(result.calculated_value);

            notification.success({
                message: valueChanged ? 'Reikšmė perskaičiuota' : 'Reikšmė nepasikeitė',
                description: `${result.message} Laikotarpis ${result.period}: ${previous} → ${calculated}.`,
            });

            if (valueChanged) {
                handleRefreshData();
            }
        } catch {
            // Klaidos pranešimą rodo API kvietimas
        } finally {
            setRecalculatingIndicatorId(null);
        }
    };

    const handleUnconfirmIndicator = (indicator: SchoolIndicatorValue) => {
        if (unconfirmingIndicatorId !== null) return;

        const effectiveScope = scope || 'school';

        if (effectiveScope === 'school' && !singleSchool?.id) {
            notification.error({
                message: 'Klaida',
                description: 'Nepavyko nustatyti mokyklos, todėl patvirtinimo atšaukti negalima',
            });
            return;
        }

        Modal.confirm({
            title: 'Atšaukti patvirtinimą',
            content: 'Ar tikrai norite atšaukti šio rodiklio patvirtinimą? Reikšmė išliks, bet patvirtinimas bus nuimtas.',
            okText: 'Atšaukti patvirtinimą',
            cancelText: 'Palikti',
            okButtonProps: { danger: true },
            onOk: async () => {
                setUnconfirmingIndicatorId(indicator.id);
                try {
                    const result = await dispatch(unconfirmIndicatorValue({
                        indicator_id: indicator.id,
                        scope: effectiveScope,
                        ...(effectiveScope === 'school' && singleSchool?.id ? { school_id: singleSchool.id } : {}),
                        date: selectedDate.format('YYYY-MM-DD'),
                    })).unwrap();

                    notification.success({
                        message: 'Patvirtinimas atšauktas',
                        description: result.message || 'Rodiklio patvirtinimas atšauktas.',
                    });
                    handleRefreshData();
                } catch {
                    // Klaidos pranešimą rodo API kvietimas
                } finally {
                    setUnconfirmingIndicatorId(null);
                }
            },
        });
    };

    const getEmptyStateMessage = () => {
        if (debouncedSearchTerm || filterOptions.valueStatus !== 'all' || filterOptions.indicatorType !== 'all' || filterOptions.criticalOnly || filterOptions.frequency !== 'all') {
            return 'Pagal pasirinktus filtrus rodiklių nerasta';
        }

        let scopeText = 'Ši kategorija';
        if (scope === 'education_center') {
            scopeText = 'Švietimo centro';
        } else if (scope === 'education_department') {
            scopeText = 'Švietimo skyriaus';
        }

        return `${scopeText} šiuo metu neturi rodiklių`;
    };

    const getEmptyStateDescription = () => {
        if (debouncedSearchTerm || filterOptions.valueStatus !== 'all' || filterOptions.indicatorType !== 'all' || filterOptions.criticalOnly || filterOptions.frequency !== 'all') {
            return 'Pabandykite pakeisti paieškos kriterijus arba išvalyti filtrus';
        }
        return 'Rodikliai bus rodomi, kai jie bus pridėti prie šios kategorijos';
    };

    // Edit mode functions
    const toggleEditMode = () => {
        if (isEditMode && hasUnsavedChanges) {
            Modal.confirm({
                title: 'Neišsaugoti pakeitimai',
                content: 'Ar tikrai norite išeiti? Neišsaugoti pakeitimai bus prarasti.',
                onOk: () => {
                    setIsEditMode(false);
                    setEditingIndicators({});
                    setHasUnsavedChanges(false);
                    setIndicatorsToConfirm(new Set());
                },
            });
        } else {
            setIsEditMode(!isEditMode);
            if (!isEditMode) {
                // Initialize editing state
                const editingState: Record<number, EditableIndicator> = {};
                const initIndicator = (indicator: any) => {
                    const isComputedDisabled = indicator.is_computed;
                    const isCriticalDisabled = indicator.is_critical && !allowedRolesForCritical.includes(userData?.role_id || 0);
                    const isConfirmedDisabled = userData?.role_id === 5 && indicator.confirmed_at;

                    if (!isComputedDisabled && !isCriticalDisabled && !isConfirmedDisabled) {
                        editingState[indicator.id] = {
                            id: indicator.id,
                            value: indicator.value ?? null,
                            unit: indicator.unit ?? null,
                            value_id: indicator.value_id ?? null,
                            isEditing: false,
                            originalValue: indicator.value ?? null,
                        };
                    }
                };
                forEachCategory(schoolIndicators, category => {
                    category.indicators?.forEach(initIndicator);
                });
                setEditingIndicators(editingState);
            }
        }
    };

    const startEditing = (indicatorId: number) => {
        setEditingIndicators(prev => {
            const indicator = prev[indicatorId];
            if (!indicator) return prev;

            return {
                ...prev,
                [indicatorId]: {
                    ...indicator,
                    isEditing: true,
                },
            };
        });
    };

    const stopEditing = (indicatorId: number) => {
        setEditingIndicators(prev => {
            const indicator = prev[indicatorId];
            if (!indicator) return prev;

            return {
                ...prev,
                [indicatorId]: {
                    ...indicator,
                    isEditing: false,
                    // Don't revert the value - keep the current value
                },
            };
        });
    };

    const cancelEditing = (indicatorId: number) => {
        setEditingIndicators(prev => {
            const indicator = prev[indicatorId];
            if (!indicator) return prev;

            return {
                ...prev,
                [indicatorId]: {
                    ...indicator,
                    isEditing: false,
                    value: indicator.originalValue, // Revert to original value
                },
            };
        });
    };

    const updateEditingValue = (indicatorId: number, value: string) => {
        setEditingIndicators(prev => {
            const indicator = prev[indicatorId];
            if (!indicator) return prev;

            return {
                ...prev,
                [indicatorId]: {
                    ...indicator,
                    value,
                },
            };
        });
        setHasUnsavedChanges(true);
    };

    const saveChanges = async () => {
        const changedIndicators = Object.values(editingIndicators)
            .filter(indicator => indicator.value !== indicator.originalValue)
            .map(indicator => ({
                id: indicator.id,
                value: indicator.value,
                unit: indicator.unit,
                value_id: indicator.value_id,
            }));

        // Get confirmed value_ids (these are already value_ids, not indicator ids)
        const confirmedValueIds = Array.from(indicatorsToConfirm);

        if (changedIndicators.length === 0 && confirmedValueIds.length === 0) {
            notification.info({
                message: 'Informacija',
                description: 'Nėra pakeitimų išsaugoti',
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload: any = {
                indicators: changedIndicators,
                confirmed_value_ids: confirmedValueIds,
            };

            if (scope === 'school') {
                payload.school_id = singleSchool?.id;
            } else {
                payload.scope = scope;
            }

            // Add category_id if activeTab is set
            if (activeTab) {
                payload.category_id = parseInt(activeTab, 10);
            }

            // Add date parameter if user can select date
            if (canSelectDate) {
                payload.date = selectedDate.format('YYYY-MM-DD');
            }

            await dispatch(updateSchoolIndicators(payload)).unwrap();

            // Update original values
            setEditingIndicators(prev => {
                const updated = { ...prev };
                changedIndicators.forEach(indicator => {
                    const editingIndicator = updated[indicator.id];
                    if (editingIndicator && indicator.value !== undefined) {
                        editingIndicator.originalValue = indicator.value;
                    }
                });
                return updated;
            });

            setHasUnsavedChanges(false);
            setIndicatorsToConfirm(new Set());

            let description = '';
            if (changedIndicators.length > 0 && confirmedValueIds.length > 0) {
                description = `Išsaugota ${changedIndicators.length} rodiklių reikšmės ir patvirtinta ${confirmedValueIds.length} rodiklių`;
            } else if (changedIndicators.length > 0) {
                description = `Išsaugota ${changedIndicators.length} rodiklių reikšmės`;
            } else if (confirmedValueIds.length > 0) {
                description = `Patvirtinta ${confirmedValueIds.length} rodiklių`;
            }

            notification.success({
                message: 'Sėkmingai išsaugota',
                description,
            });
        } catch (error) {
            notification.error({
                message: 'Klaida',
                description: 'Nepavyko išsaugoti pakeitimų',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const discardChanges = () => {
        Modal.confirm({
            title: 'Atmesti pakeitimus',
            content: 'Ar tikrai norite atmesti visus pakeitimus?',
            onOk: () => {
                setEditingIndicators(prev => {
                    const reverted = { ...prev };
                    Object.keys(reverted).forEach(key => {
                        const id = parseInt(key, 10);
                        const indicator = reverted[id];
                        if (indicator) {
                            reverted[id] = {
                                ...indicator,
                                value: indicator.originalValue,
                                isEditing: false,
                            };
                        }
                    });
                    return reverted;
                });
                setHasUnsavedChanges(false);
                setIndicatorsToConfirm(new Set());
            },
        });
    };

    const handleKeyPress = (e: React.KeyboardEvent, indicatorId: number) => {
        if (e.key === 'Enter') {
            stopEditing(indicatorId);
        } else if (e.key === 'Escape') {
            cancelEditing(indicatorId);
        }
    };

    const handleConfirmationToggle = (indicatorId: number) => {
        // Find the indicator to check if it has a value and get value_id
        let indicatorHasValue = false;
        let valueId: number | null | undefined = null;

        const checkIndicator = (indicator: any) => {
            if (indicator.id === indicatorId) {
                indicatorHasValue = indicator.value !== null && indicator.value !== undefined && indicator.value !== '';
                valueId = indicator.value_id;
            }
        };

        forEachCategory(schoolIndicators, category => {
            category.indicators?.forEach(checkIndicator);
        });

        // Only allow confirmation if indicator has a value and value_id
        if (!indicatorHasValue || !valueId) {
            notification.warning({
                message: 'Negalima patvirtinti',
                description: 'Rodiklio reikšmė turi būti nustatyta prieš patvirtinimą',
            });
            return;
        }

        setIndicatorsToConfirm(prev => {
            const newSet = new Set(prev);
            if (valueId && newSet.has(valueId)) {
                newSet.delete(valueId);
            } else if (valueId) {
                newSet.add(valueId);
            }
            return newSet;
        });
        setHasUnsavedChanges(true);
    };

    const renderEditableValue = (indicator: any) => {
        if (!indicator.id) {
            return formatValue(indicator.value, indicator.unit);
        }

        const editingIndicator = editingIndicators[indicator.id];
        const isDisabled = indicator.is_computed
            || (indicator.is_critical && !allowedRolesForCritical.includes(userData?.role_id || 0))
            || (userData?.role_id === 5 && indicator.confirmed_at);

        if (!isEditMode || isDisabled) {
            // Check if disabled due to confirmed status for role 5
            const isConfirmedDisabled = userData?.role_id === 5 && indicator.confirmed_at;

            if (isConfirmedDisabled) {
                return (
                    <Tooltip title="Patvirtintos reikšmės keisti nebegalite. Jei manote, kad įvyko klaida, susisiekite su švietimo skyriumi.">
                        <div style={{ cursor: 'not-allowed', opacity: 0.7 }}>
                            {formatValue(indicator.value, indicator.unit)}
                        </div>
                    </Tooltip>
                );
            }

            return (
                <div className="indicator-value-container">
                    {formatValue(indicator.value, indicator.unit)}
                    {editingIndicator && !indicator.confirmed_at && indicator.value !== null && indicator.value !== undefined && indicator.value !== '' && (
                        <div className="confirmation-checkbox">
                            <Checkbox
                                checked={indicatorsToConfirm.has(indicator.id)}
                                onChange={() => handleConfirmationToggle(indicator.id)}>
                                <span className="confirmation-label">Patvirtinti</span>
                            </Checkbox>
                        </div>
                    )}
                </div>
            );
        }

        if (editingIndicator?.isEditing) {
            const renderInput = () => {
                if (indicator.unit === 'taip_ne') {
                    return (
                        <Select
                            value={editingIndicator.value || undefined}
                            onChange={(value) => updateEditingValue(indicator.id, value || '')}
                            onBlur={() => stopEditing(indicator.id)}
                            onKeyDown={(e) => handleKeyPress(e, indicator.id)}
                            autoFocus
                            size="small"
                            style={{ width: 120 }}>
                            <Option value="1.000">Taip</Option>
                            <Option value="0.000">Ne</Option>
                        </Select>
                    );
                }

                if (indicator.unit === 'text') {
                    return (
                        <Input.TextArea
                            value={editingIndicator.value || ''}
                            onChange={(e) => updateEditingValue(indicator.id, e.target.value)}
                            onBlur={() => stopEditing(indicator.id)}
                            onKeyDown={(e) => handleKeyPress(e, indicator.id)}
                            autoFocus
                            size="small"
                            style={{ width: 300, minHeight: 80 }}
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            placeholder="Įveskite tekstą..." />
                    );
                }

                return (
                    <Input
                        value={editingIndicator.value || ''}
                        onChange={(e) => updateEditingValue(indicator.id, e.target.value)}
                        onBlur={() => stopEditing(indicator.id)}
                        onKeyDown={(e) => handleKeyPress(e, indicator.id)}
                        autoFocus
                        size="small"
                        style={{ width: 120 }}
                        onKeyPress={(e) => {
                            const { charCode } = e;
                            const isDigit = charCode >= 48 && charCode <= 57;
                            const isComma = charCode === 44;
                            const isDot = charCode === 46;
                            const isSlash = charCode === 47;

                            if (
                                indicator.unit === 'ratio' && !(isDigit || isSlash)
                                || indicator.unit !== 'ratio' && !(isDigit || isDot || isComma)
                            ) {
                                e.preventDefault();
                            }
                        }} />
                );
            };

            return (
                <div className="inline-edit-wrapper">
                    <span className="indicator-unit-symbol">
                        {getUnitSymbol(indicator.unit)}
                    </span>
                    {renderInput()}
                </div>
            );
        }

        return (
            <div className="indicator-value-container">
                <div
                    className={`editable-value ${editingIndicator?.value !== editingIndicator?.originalValue ? 'has-changes' : ''}`}
                    onClick={() => startEditing(indicator.id)}
                    style={{ cursor: 'pointer' }}>
                    {formatValue(editingIndicator?.value || indicator.value, indicator.unit)}
                </div>
                {!indicator.confirmed_at && indicator.value !== null && indicator.value !== undefined && indicator.value !== '' && (
                    <div className="confirmation-checkbox">
                        <Checkbox
                            checked={indicatorsToConfirm.has(indicator.value_id || 0)}
                            onChange={() => handleConfirmationToggle(indicator.id)}>
                            <span className="confirmation-label">Patvirtinti</span>
                        </Checkbox>
                    </div>
                )}
            </div>
        );
    };

    const hasActiveFilters = filterOptions.valueStatus !== 'all' || filterOptions.indicatorType !== 'all' || filterOptions.criticalOnly || filterOptions.frequency !== 'all';

    const renderCategoryContent = (category: SchoolIndicator) => (
        <div key={`display_content_${category.id}`}>
            <Card className="category-header-card">
                <Row justify="space-between" align="middle">
                    <Col>
                        <h2 className="category-title">Veiksmai:</h2>
                    </Col>
                    <Col>
                        {isEditMode ? (
                            <Space>
                                <Button
                                    icon={<UndoOutlined />}
                                    onClick={discardChanges}
                                    disabled={!hasUnsavedChanges}>
                                    Atmesti
                                </Button>
                                <Button
                                    icon={<CloseOutlined />}
                                    onClick={toggleEditMode}>
                                    Baigti redagavimą
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={saveChanges}
                                    loading={isSaving}
                                    disabled={!hasUnsavedChanges}>
                                    Išsaugoti
                                </Button>
                            </Space>
                        ) : (
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={toggleEditMode}>
                                Redaguoti
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            {isEmpty(category.indicators) ? (
                <Card className="empty-state-card">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={(
                            <div className="empty-state-content">
                                <h3>{getEmptyStateMessage()}</h3>
                                <p>{getEmptyStateDescription()}</p>
                                {(debouncedSearchTerm || filterOptions.valueStatus !== 'all' || filterOptions.indicatorType !== 'all' || filterOptions.criticalOnly || filterOptions.frequency !== 'all') && (
                                    <Button
                                        type="primary"
                                        onClick={handleFilterReset}
                                        style={{ marginTop: 16 }}>
                                        Išvalyti filtrus
                                    </Button>
                                )}
                            </div>
                        )} />
                </Card>
            ) : null}

            <Row gutter={16}>
                {!isEmpty(category.indicators) ? (
                    category.indicators.map((indicator) => {
                        const status = getIndicatorStatus(indicator);
                        const isDisabled = indicator.is_computed
                            || (indicator.is_critical && !allowedRolesForCritical.includes(userData?.role_id || 0))
                            || (userData?.role_id === 5 && indicator.confirmed_at);

                        return (
                            <Col span={8} key={indicator.id} xs={24} sm={24} md={24} lg={12} xl={8}>
                                <Card
                                    key={indicator.id}
                                    className={`indicator-card ${isEditMode && !isDisabled ? 'editable' : ''} ${indicator.confirmed_at ? 'confirmed' : ''}`}
                                    hoverable>
                                    <div className="indicator-card-header">
                                        <div className="indicator-title-section">
                                            <h4 className="indicator-title notranslate" translate="no">
                                                {indicator.title} <Tag color="blue" className="indicator-id notranslate" translate="no">R{indicator.id}</Tag>
                                                {indicator.unit && (
                                                    <Tooltip title={<span className="notranslate" translate="no">{indicator.unit_description || indicator.unit}</span>}>
                                                        <span className="indicator-unit-tooltip notranslate" translate="no">({getUnitSymbol(indicator.unit)})</span>
                                                    </Tooltip>
                                                )}
                                                {indicator.is_critical && (
                                                    <Tooltip title={<span className="notranslate" translate="no">Tai kritinis rodiklis. Jį gali pildyti tik švietimo skyrius, švietimo centras arba administratoriai.</span>}>
                                                        <Tag color="red" className="critical-tag notranslate" translate="no">Kritinis</Tag>
                                                    </Tooltip>
                                                )}
                                            </h4>
                                            {indicator.input_frequency && renderFrequencyDisplay(indicator.input_frequency)}
                                            {indicator.input_frequency && indicator.period_description && renderFrequencyDescription(indicator.input_frequency, indicator.period_description)}
                                            {indicator.collection_start && indicator.collection_end && (() => {
                                                const missed = isCollectionPeriodMissed(indicator);
                                                const tooltipText = missed
                                                    ? `Praleistas pildymo laikotarpis! Duomenys vedami nuo ${indicator.collection_start} iki ${indicator.collection_end}`
                                                    : `Duomenys vedami nuo ${indicator.collection_start} iki ${indicator.collection_end}`;
                                                return (
                                                    <Tooltip title={tooltipText}>
                                                        <Tag color={missed ? 'red' : 'cyan'} className="collection-period-tag">
                                                            <CalendarOutlined /> {indicator.collection_start} – {indicator.collection_end}
                                                        </Tag>
                                                    </Tooltip>
                                                );
                                            })()}
                                            {indicator.confirmed_at && (
                                                <div className="confirmation-row">
                                                    {renderConfirmationTag(indicator)}
                                                    {canUnconfirmConfirmation && (
                                                        <Tooltip title="Atšaukti šio rodiklio patvirtinimą. Reikšmė išliks, bet patvirtinimas bus nuimtas ir reikšmę vėl bus galima keisti.">
                                                            <Button
                                                                size="small"
                                                                danger
                                                                className="unconfirm-button"
                                                                aria-label="Atšaukti patvirtinimą"
                                                                icon={<CloseCircleOutlined />}
                                                                loading={unconfirmingIndicatorId === indicator.id}
                                                                disabled={unconfirmingIndicatorId !== null && unconfirmingIndicatorId !== indicator.id}
                                                                onClick={() => handleUnconfirmIndicator(indicator)}>
                                                                Atšaukti
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="indicator-header-actions">
                                            <div className="indicator-header-actions-row">
                                                <Button
                                                    size="small"
                                                    title="Peržiūrėti istoriją"
                                                    icon={<HistoryOutlined />}
                                                    onClick={() => {
                                                        setLogModalVisible(true);
                                                        dispatch(getSchoolIndicatorLogs({
                                                            indicator_value_id: indicator?.value_id,
                                                        }));
                                                    }} />
                                                <Tag
                                                    color={getStatusColor(status.type)}
                                                    icon={status.icon}
                                                    className="status-tag">
                                                    {status.text}
                                                </Tag>
                                            </div>
                                            {indicator.is_computed && (
                                                <Tooltip title="Perskaičiuoja rodiklio reikšmę pagal formulę ir patikrina, ar saugoma reikšmė pasikeitė">
                                                    <Button
                                                        size="small"
                                                        className="recalculate-button"
                                                        aria-label="Perskaičiuoti rodiklio reikšmę"
                                                        icon={(
                                                            <span className="recalculate-icons">
                                                                <CalculatorOutlined />
                                                                <SyncOutlined />
                                                            </span>
                                                        )}
                                                        loading={recalculatingIndicatorId === indicator.id}
                                                        disabled={recalculatingIndicatorId !== null && recalculatingIndicatorId !== indicator.id}
                                                        onClick={() => handleRecalculateIndicator(indicator)} />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    <div className="indicator-value-section">
                                        {renderEditableValue(indicator)}
                                    </div>

                                    {(indicator.description || indicator.source_document || indicator.data_source || indicator.target_value || (indicator.target_values && indicator.target_values.length > 0) || indicator.is_computed || indicator.unit_description || indicator.input_frequency) && (
                                        <Collapse
                                            ghost
                                            size="small"
                                            className="info-collapse">
                                            <Panel
                                                header={(
                                                    <span className="collapse-header">
                                                        <InfoCircleOutlined />
                                                        Daugiau informacijos
                                                    </span>
                                                )}
                                                key="info">
                                                <div className="indicator-info-content">
                                                    {indicator.description && (
                                                        <div className="indicator-description">
                                                            <strong>Aprašymas:</strong>
                                                            <p>{indicator.description}</p>
                                                        </div>
                                                    )}

                                                    {indicator.source_document && (
                                                        <div className="indicator-description">
                                                            <strong>Iš kokio dokumento paimtas rodiklis:</strong>
                                                            <p>{indicator.source_document}</p>
                                                        </div>
                                                    )}

                                                    {indicator.data_source && (
                                                        <div className="indicator-description">
                                                            <strong>Duomenų šaltinis:</strong>
                                                            <p>{indicator.data_source}</p>
                                                        </div>
                                                    )}

                                                    {indicator.target_value && (
                                                        <div className="indicator-description">
                                                            <strong>Tikslinė reikšmė (rezultatas, kurį reikia pasiekti):</strong>
                                                            <p>{indicator.target_value}</p>
                                                        </div>
                                                    )}

                                                    {indicator.target_values && indicator.target_values.length > 0 && (
                                                        <div className="indicator-target-values">
                                                            <strong>Tikslinės reikšmės (pagal laikotarpius):</strong>
                                                            <ul>
                                                                {indicator.target_values.map((target: IndicatorTargetValue, idx: number) => (
                                                                    <li key={target.id || `${target.period}-${idx}`}>
                                                                        <span>{target.period ? `${target.period} m.m. : ` : ''}{target.target_value}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {indicator.unit_description && (
                                                        <div className="indicator-unit-description">
                                                            <strong>Vienetas:</strong>
                                                            <p>{indicator.unit_description}</p>
                                                        </div>
                                                    )}

                                                    {indicator.input_frequency && (
                                                        <div className="indicator-frequency">
                                                            <strong>Duomenų suvedimo dažnumas:</strong>
                                                            <p>{frequencyOptions[indicator.input_frequency as keyof typeof frequencyOptions]?.description || indicator.input_frequency}</p>
                                                        </div>
                                                    )}

                                                    {indicator.is_computed && (
                                                        <div className="computed-info">
                                                            <strong>Skaičiuojamas rodiklis</strong>
                                                            {indicator.formula && (
                                                                <div className="formula-section">
                                                                    <strong>Formulė:</strong>
                                                                    <code>{indicator.formula}</code>
                                                                </div>
                                                            )}
                                                            {indicator.depends_on && indicator.depends_on.length > 0 && (
                                                                <div className="dependencies-section">
                                                                    <strong>Rodikliai, nuo kurių priklauso:</strong>
                                                                    <ul>
                                                                        {indicator.depends_on.map((dep) => (
                                                                            <li key={dep.id}>{dep.title} (R{dep.id})</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            <p className="computed-note">
                                                                <em>Šis rodiklis bus apskaičiuotas, kai mokykla pateiks visus reikalingus duomenis.</em>
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </Panel>
                                        </Collapse>
                                    )}
                                </Card>
                            </Col>
                        );
                    })
                ) : null}
            </Row>
        </div>
    );

    // Find active category/subcategory title at any nesting depth
    const findCategoryTitle = (tabId: string): string | undefined => {
        let title: string | undefined;
        forEachCategory(schoolIndicators, cat => {
            if (cat.id.toString() === tabId) {
                title = cat.title;
            }
        });
        return title;
    };

    // Check if a category or any of its descendants is the active tab
    const isInActiveSubtree = (cat: SchoolIndicator): boolean => {
        if (cat.id.toString() === activeTab) return true;
        return cat.children?.some(isInActiveSubtree) || false;
    };

    // Build flat tab items — only expand children for the active branch
    const items: TabsProps['items'] = [];
    const addCategoryItems = (categories: SchoolIndicator[], depth = 0) => {
        categories.forEach((category: SchoolIndicator) => {
            const hasChildren = category.children && category.children.length > 0;
            const isChild = depth > 0;

            items.push({
                key: category.id.toString(),
                label: (
                    <div className={`tab-label${isChild ? ' tab-label-child' : ''}`} style={depth > 1 ? { paddingLeft: 16 * depth } : undefined}>
                        <span>{category.title}</span>
                        <span className="indicator-count">
                            ({category.indicators_count})
                            {hasChildren && <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />}
                        </span>
                    </div>
                ),
                children: renderCategoryContent(category),
            });

            if (hasChildren && isInActiveSubtree(category)) {
                addCategoryItems(category.children!, depth + 1);
            }
        });
    };
    addCategoryItems(filteredSchoolIndicators);

    return (
        <div className="school-indicators-wrapper">
            <Card className="header-card">
                <Row justify="space-between" align="middle" className="header-actions">
                    <Col>
                        {/* Selected Category Display */}
                        {(activeTab || schoolIndicators.length > 0) && (
                            <div style={{ marginTop: 8 }}>
                                <h2 className="category-title-header">
                                    {activeTab
                                        ? `${findCategoryTitle(activeTab) || 'Nežinoma kategorija'}: `
                                        : `${schoolIndicators[0]?.title}: ` || 'Nėra kategorijų'}
                                </h2>
                            </div>
                        )}
                    </Col>
                    <Col>
                        <Space>
                            <MissingIndicatorsButton />
                            {canSelectDate && (
                                <DatePicker
                                    value={selectedDate}
                                    onChange={(date) => { setSelectedDate(date || dayjs()); setIsEditMode(false); }}
                                    format="YYYY-MM-DD"
                                    placeholder="Pasirinkite datą"
                                    style={{ width: 150, height: 40 }}
                                    allowClear={false}
                                    disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            )}
                            <Input
                                placeholder="Ieškoti rodiklių..."
                                prefix={<SearchOutlined />}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                allowClear
                                className="search-input" />
                            <Button
                                icon={<FilterOutlined />}
                                onClick={() => setFilterPanelVisible(!filterPanelVisible)}
                                type={hasActiveFilters ? 'primary' : 'default'}
                                className="filter-button">
                                Filtras
                                {hasActiveFilters && <Tag color="red" style={{ marginLeft: 4, fontSize: 10 }}>ON</Tag>}
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefreshData}
                                loading={isFetching}
                                title="Atnaujinti duomenis"
                                className="refresh-button" />
                        </Space>
                    </Col>
                </Row>

                {/* Expandable Filter Panel */}
                {filterPanelVisible && (
                    <div className="filter-panel">
                        <div className="filter-panel-header">
                            <h4>Filtravimo nustatymai</h4>
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => setFilterPanelVisible(false)}
                                size="small" />
                        </div>
                        <div className="filter-panel-content">
                            <Row gutter={16}>
                                <Col span={6}>
                                    <label className="filter-label">Reikšmės statusas:</label>
                                    <Select
                                        value={filterOptions.valueStatus}
                                        onChange={(value) => setFilterOptions(prev => ({ ...prev, valueStatus: value }))}
                                        style={{ width: '100%' }}>
                                        <Option value="all">Visi rodikliai</Option>
                                        <Option value="filled">Užpildyti</Option>
                                        <Option value="empty">Neužpildyti</Option>
                                    </Select>
                                </Col>
                                <Col span={6}>
                                    <label className="filter-label">Rodiklio tipas:</label>
                                    <Select
                                        value={filterOptions.indicatorType}
                                        onChange={(value) => setFilterOptions(prev => ({ ...prev, indicatorType: value }))}
                                        style={{ width: '100%' }}>
                                        <Option value="all">Visi tipai</Option>
                                        <Option value="manual">Rankinis įvedimas</Option>
                                        <Option value="computed">Skaičiuojami</Option>
                                    </Select>
                                </Col>
                                <Col span={6}>
                                    <label className="filter-label">Duomenų suvedimo dažnumas:</label>
                                    <Select
                                        value={filterOptions.frequency}
                                        onChange={(value) => setFilterOptions(prev => ({ ...prev, frequency: value }))}
                                        style={{ width: '100%' }}>
                                        <Option value="all">Visi dažnumai</Option>
                                        {Object.entries(frequencyOptions).map(([key, option]) => (
                                            <Option key={key} value={key}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={6}>
                                    <div className="checkbox-filter">
                                        <Checkbox
                                            checked={filterOptions.criticalOnly}
                                            onChange={(e) => setFilterOptions(prev => ({ ...prev, criticalOnly: e.target.checked }))}>
                                            Tik kritiniai rodikliai
                                        </Checkbox>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                )}

                <Row gutter={16} className="statistics-row" justify="space-between">
                    <Col>
                        <Statistic
                            title="Iš viso"
                            value={statistics.total}
                            valueStyle={{ color: '#fdab01' }} />
                    </Col>
                    <Col>
                        <Statistic
                            title="Užpildyta"
                            value={statistics.filled}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<CheckCircleOutlined />} />
                    </Col>
                    <Col>
                        <Statistic
                            title="Neužpildyta"
                            value={statistics.empty}
                            valueStyle={{ color: '#fa8c16' }}
                            prefix={<ExclamationCircleOutlined />} />
                    </Col>
                    <Col>
                        <Statistic
                            title="Skaičiuojami"
                            value={statistics.computed}
                            valueStyle={{ color: '#722ed1' }}
                            prefix={<CalculatorOutlined />} />
                    </Col>
                    <Col>
                        <Statistic
                            title="Kritiniai rodikliai"
                            value={statistics.critical}
                            valueStyle={{ color: '#f5222d' }}
                            prefix={<ExclamationCircleOutlined />} />
                    </Col>
                </Row>
            </Card>

            <Spin spinning={isFetching}>
                <div className={`school-indicators-tabs-wrapper${tabsCollapsed ? ' tabs-collapsed' : ''}`}>
                    {tabsCollapsed && (
                        <button
                            type="button"
                            className="tabs-expand-btn"
                            onClick={() => setTabsCollapsed(false)}
                            title="Išskleisti kategorijas"
                            aria-label="Išskleisti kategorijas">
                            <MenuUnfoldOutlined />
                        </button>
                    )}
                    <Tabs
                        tabPosition="left"
                        items={items}
                        {...(activeTab ? { activeKey: activeTab } : {})}
                        onChange={setActiveTab}
                        className="school-indicators-tabs"
                        {...(!tabsCollapsed && {
                            tabBarExtraContent: {
                                left: (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<MenuFoldOutlined />}
                                        onClick={() => setTabsCollapsed(true)}
                                        title="Sutraukti kategorijas"
                                        className="tabs-collapse-btn"
                                        aria-label="Sutraukti kategorijas" />
                                ),
                            },
                        })} />
                </div>

                {filteredSchoolIndicators.length === 0 && (
                    <Card className="global-empty-state-card">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={(
                                <div className="global-empty-state-content">
                                    <h3>{getEmptyStateMessage()}</h3>
                                    <p>{getEmptyStateDescription()}</p>
                                    {(debouncedSearchTerm || filterOptions.valueStatus !== 'all' || filterOptions.indicatorType !== 'all' || filterOptions.criticalOnly || filterOptions.frequency !== 'all') && (
                                        <Button
                                            type="primary"
                                            onClick={handleFilterReset}
                                            style={{ marginTop: 16 }}>
                                            Išvalyti filtrus
                                        </Button>
                                    )}
                                    {!debouncedSearchTerm && filterOptions.valueStatus === 'all' && filterOptions.indicatorType === 'all' && !filterOptions.criticalOnly && (
                                        <div className="empty-state-actions">
                                            <p className="empty-state-hint">
                                                Jei manote, kad rodikliai turėtų būti rodomi, susisiekite su administratoriumi.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )} />
                    </Card>
                )}
            </Spin>

            <Modal
                open={logModalVisible}
                onCancel={() => setLogModalVisible(false)}
                footer={null}
                title={(
                    <div className="log-modal-title">
                        <HistoryOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                        Rodiklio istorija
                    </div>
                )}
                width={800}
                className="log-modal">
                <Spin spinning={isFetching}>
                    {isEmpty(schoolIndicatorLogs) ? (
                        <Empty
                            description="Istorijos įrašų nėra"
                            image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <div className="indicator-log-list">
                            {schoolIndicatorLogs.map((log) => (
                                <div key={log.id} className="indicator-log-entry">
                                    <div className="log-entry-header">
                                        <div className="log-entry-meta">
                                            <div className="log-action-badge">
                                                {log.action === 'created' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                                {log.action === 'updated' && <EditOutlined style={{ color: '#fdab01' }} />}
                                                {log.action === 'deleted' && <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                                                {log.action === 'compute_error' && <WarningOutlined style={{ color: '#fa8c16' }} />}
                                                <span className="action-text">{actionLabels[log.action] || log.action}</span>
                                            </div>
                                            <div className="log-timestamp">
                                                <ClockCircleOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
                                                {log.created_at}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="log-entry-content">
                                        <div className="log-user-info">
                                            <div className="user-avatar">
                                                <UserOutlined />
                                            </div>
                                            <div className="user-details">
                                                <div className="user-name">{log.user?.name || 'Nežinomas vartotojas'}</div>
                                                <div className="user-email">{log.user?.email}</div>
                                                {log.user?.school && (
                                                    <div className="user-school">
                                                        <BankOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
                                                        {log.user.school.title}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {(log.old_value !== null || log.new_value !== null) && (
                                            <div className="log-value-changes">
                                                <div className="value-change-item">
                                                    <div className="value-label old-value">
                                                        <MinusOutlined style={{ color: '#ff4d4f' }} />
                                                        <span>Ankstesnė reikšmė:</span>
                                                    </div>
                                                    <div className="value-content">
                                                        {log.old_value ?? <em className="no-value">nėra</em>}
                                                    </div>
                                                </div>
                                                <div className="value-change-item">
                                                    <div className="value-label new-value">
                                                        <PlusOutlined style={{ color: '#52c41a' }} />
                                                        <span>Nauja reikšmė:</span>
                                                    </div>
                                                    <div className="value-content">
                                                        {log.new_value ?? <em className="no-value">nėra</em>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Spin>
            </Modal>
        </div>
    );
};

SchoolIndicatorsData.defaultProps = {
    scope: 'school',
};

export default SchoolIndicatorsData;
